import {t} from 'ttag';
import PropTypes from "prop-types";
import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    CircularProgress,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Tab,
    Tabs,
    useMediaQuery
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close'
import Autocomplete from "@mui/material/Autocomplete";
import OpenWindowIcon from "../../../Icons/OpenWindowIcon";
import {TextInput} from "../../../index";
import useStyles from "./AirportSelectorStyles";
import {filterAirportByRegionCode, filterAirports, sortByRegionCode} from "./airportsTools";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {useSelector} from "react-redux";

const AirportSelector = ({
                             options,
                             label,
                             placeholder,
                             variant = 'azo',
                             leftRadius = false,
                             rightRadius = false,
                             onChange,
                             disabled = false,
                             value: selectedValue,
                             autoFocus = false,
                             onFocus,
                             onBlur,
                             inputRef,
                             noOptionsText,
                             optionField = 'name',
                             compressed = false,
                             hideIcon = false,
                             loading,
                             flat = false,
                             error = false,
                             noBorder = false,
                             ...props
                         }) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [highlightedSection, setHighlightedSection] = useState('list');
    const [filteredOptions, setFilteredOptions] = useState(sortByRegionCode(options));
    const inputAnchorRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [selectedTab, setSelectedTab] = useState(0);
    const listRef = useRef();
    const uniqueOptions = sortByRegionCode(options?.filter((region, index, self) =>
        index === self.findIndex((r) => r.regionCode === region.regionCode)
    ));
    const popularAirports = useSelector(state => state.sw.popularAirports);
    const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const listItemRefs = useRef([]);
    listItemRefs.current = [];

    const filterOptions = (options, inputValue) => (
        options?.filter(option => filterAirports(option, inputValue))
            ?.sort((a, b) => {
                if (a.code?.toLowerCase() === inputValue.toLowerCase()) return -1;
                if (b.code?.toLowerCase() === inputValue.toLowerCase()) return 1;
                return 0;
            })
            ?.sort((a, b) => {
                if (a.name?.toLowerCase() === inputValue.toLowerCase()) return -1;
                if (b.name?.toLowerCase() === inputValue.toLowerCase()) return 1;
                return 0;
            })
    );

    const handleListItemRef = (index) => (ref) => {
        if (ref) {
            listItemRefs.current[index] = ref;
        }
    };

    useEffect(() => {
        if (open) {
            setHighlightedIndex(0);
            setHighlightedSection('list');
        }
        if (isMobile && open) {
            setSelectedTab(null);
            setHighlightedSection('tabs');
        }
    }, [isMobile, open]);

    useEffect(() => {
        const result = filterOptions(options, inputValue);
        setFilteredOptions(result);
    }, [inputValue, options]);

    useEffect(() => {
        if (highlightedSection === 'list') {
            setHighlightedIndex(0);
        }
    }, [filteredOptions, selectedValue, highlightedSection]);

    useEffect(() => {
        const el = document.querySelector('.azo-airport-list-popup button:nth-of-type(' + (highlightedIndex + 1) + ')');
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    const handleOnFocus = e => {
        if (onFocus) onFocus(e);
        setOpen(true);
    };

    const handleBlur = e => {
        if (onBlur) onBlur(e);
        setOpen(false);
    }

    const handleInputChange = (event, newInputValue) => {
        const inputValue = newInputValue.replace(/^\s+/, "");

        setInputValue(inputValue);

        setSelectedTab(0);

        const result = filterOptions(options, inputValue);

        if (isMobile && inputValue) {
            setSelectedTab(0);
        }

        if (inputValue === '') {
            isMobile && setSelectedTab(null);
            onChange(null);
        }

        setFilteredOptions(result);
    };

    const handleFocus = (value, regionCode) => {
        setSelectedTab(value);
        const result = filterOptions(options, inputValue);
        if (value === 0) {
            setFilteredOptions(result);
            return;
        }
        if (value === 1) {
            const filtered = result?.filter(option => popularAirports.includes(option.code));
            setFilteredOptions(filtered);
            return;
        }
        const filtered = result?.filter(option => filterAirportByRegionCode(option, regionCode));
        setFilteredOptions(filtered);
    }

    const getFilteredOptionsTabLenght = (regionCode) => {
        let result = options;
        if (regionCode !== 'all') {
            result = options?.filter(r => r.regionCode === regionCode)
        }
        if (regionCode === 'popular' && popularAirports) {
            result = options?.filter(r => popularAirports.includes(r.code));
        }
        const filtered = result?.filter(option => filterAirports(option, inputValue));
        return filtered?.length;
    }

    return (
      <Box className={"azo-airport-selector"}>
        <Autocomplete
          open={false}
          disablePortal={true}
          inputValue={inputValue}
          options={options}
          id={'teste'}
          getOptionLabel={(option) => {
            if (option) {
              return `${option?.name}`;
            }
            return "";
          }}
          isOptionEqualToValue={(option, value) => {
            if (option && value) {
              return option.code === value.code;
            }
            return false;
          }}
          onInputChange={(e, v) => {
            if (!e && !v && !open) handleInputChange(e, v);
            if (e?.relatedTarget?.classList?.contains("MuiTab-root") || (!e && !v))
              return;
            handleInputChange(e, v);
          }}
          aria-label={label}
          aria-labelledby={label ? `${label}-label` : undefined}
          aria-invalid={error ? "true" : "false"}
          popupIcon={
            !hideIcon ? (
              loading ? (
                <CircularProgress size={20} />
              ) : open ? (
                <CloseIcon
                  onClick={() => {
                    isMobile && setSelectedTab(null);
                    setInputValue("");
                    setFilteredOptions(options);
                    onChange(null);
                    setTimeout(() => setOpen(false), 50);
                  }}
                  color={"secondary"}
                />
              ) : (
                <OpenWindowIcon color={"secondary"} />
              )
            ) : null
          }
          renderInput={(params) => (
            <TextInput
              {...params}
              variant={variant}
              autoFocus={autoFocus}
              placeholder={placeholder}
              label={!flat ? label : ""}
              shrink={!compressed}
              flat={flat}
              error={error}
              ariaDisabled={disabled}
              sx={{ border: noBorder ? "none !important" : "inherit" }}
              inputProps={{
                ...params.inputProps,
                "aria-controls": params.id,
              }}
              inputRef={(node) => {
                params.InputProps.ref(node);
                inputAnchorRef.current = node;
              }}
              onKeyDown={(e) => {
                if (!open) return;

                if (e.key === "ArrowLeft" && !isMobile) {
                    setHighlightedIndex(null);
                    setHighlightedSection('tabs');
                }

                if (e.key === "ArrowRight" && !isMobile) {
                    setHighlightedSection('list');
                }

                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  if (highlightedSection === 'tabs' && !isMobile) {
                      const nextTab = selectedTab + 1 < uniqueOptions.length + 2 ? selectedTab + 1 : 0;
                      const regionCode = nextTab > 1 ? uniqueOptions[nextTab - 2]?.regionCode : nextTab === 0 ? 'all' : 'popular';
                      handleFocus(nextTab, regionCode);
                      return;
                  }
                  if (!isMobile || (isMobile && selectedTab !== null)) {
                      const newIndex =
                          highlightedIndex + 1 < filteredOptions.length
                              ? highlightedIndex + 1
                              : 0;
                      setHighlightedIndex(newIndex);
                      setTimeout(() => {
                          const el = listItemRefs.current[newIndex];
                          if (el) {
                              el.scrollIntoView({
                                  behavior: "smooth",
                                  block: "nearest",
                              });
                          }
                      }, 0);
                      return;
                  }
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  if (highlightedSection === 'tabs' && !isMobile) {
                      const nextTab = selectedTab - 1 >= 0 ? selectedTab - 1 : 0;
                      const regionCode = nextTab > 1 ? uniqueOptions[nextTab - 2]?.regionCode : nextTab === 0 ? 'all' : 'popular';
                      handleFocus(nextTab, regionCode);
                      return;
                  }
                  if (!isMobile || (isMobile && selectedTab !== null)) {
                      const newIndex =
                          highlightedIndex - 1 >= 0
                              ? highlightedIndex - 1
                              : filteredOptions.length - 1;
                      setHighlightedIndex(newIndex);
                      setTimeout(() => {
                          const el = listItemRefs.current[newIndex];
                          if (el) {
                              el.scrollIntoView({
                                  behavior: "smooth",
                                  block: "nearest",
                              });
                          }
                      }, 0);
                      return;
                  }
                }

                if ((e.key === "Enter" || e.key === "Tab") || (isMobile && selectedTab !== null)) {
                  e.preventDefault();
                  if (highlightedSection === 'tabs' && !isMobile) {
                     setHighlightedSection('list');
                     return;
                  }
                  const selected = filteredOptions[highlightedIndex];
                  if (selected) {
                    onChange(selected);
                    setOpen(false);
                  }
                }

                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
          )}
          disableClearable
          selectOnFocus
          autoHighlight
          onFocus={handleOnFocus}
          onBlur={(e) => {
            const popover = document.querySelector(".azo-airport-list-popup");
            if (popover && popover.contains(e.relatedTarget)) {
              return;
            }
            handleBlur(e);
          }}
          getOptionKey={(option) => option.code}
          disabled={disabled}
          value={selectedValue || null}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          classes={
            variant === "azo"
              ? {
                  paper: classes.paper,
                  inputRoot: classes.inputRoot,
                  input: [classes.input, flat ? classes.inputFlatted : ""].join(
                    " "
                  ),
                  popupIndicator: classes.popupIndicator,
                  option: classes.option,
                }
              : {}
          }
          loading={loading}
          {...props}
        />
        {open &&
          !loading &&
          (options.length > 0 ? (
            <Popper
              open={open}
              anchorEl={inputAnchorRef.current}
              sx={{
                minWidth: isMobile ? "auto" : 525,
                width: inputAnchorRef.current?.offsetWidth,
              }}
              onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
              }}
              onBlur={() => setOpen(false)}
              placement={
                label === "to" || label === "parra" || label === "vers"
                  ? "bottom-end"
                  : "bottom-start"
              }
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 2],
                  },
                },
              ]}
              style={{ zIndex: 1500, pointerEvents: "auto" }}
            >
              <Box
                component={Paper}
                className={"azo-airport-list-popup"}
                elevation={2}
              >
                <Box sx={styles.root(isMobile)}>
                  {((isMobile && selectedTab === null) || !isMobile) && (
                    <Tabs
                      orientation="vertical"
                      variant="scrollable"
                      sx={{ ...styles.rootTab(isMobile) }}
                      slotProps={{
                        tabIndex: -1,
                      }}
                      TabIndicatorProps={{
                        style: { display: "none" },
                      }}
                      value={0}
                      selectionFollowsFocus={true}
                      tabIndex={-1}
                    >
                      <Tab
                        key={0}
                        disableRipple={isMobile}
                        sx={styles.tab(selectedTab === 0, isMobile)}
                        onMouseOver={() => !isMobile && handleFocus(0, "all")}
                        onClick={() => isMobile && handleFocus(0, "all")}
                        label={
                          t`All Regions` +
                          ` (${getFilteredOptionsTabLenght("all")})`
                        }
                        icon={
                          <KeyboardArrowRightIcon
                            color={
                              selectedTab === 0 ? "white" : "secondary"
                            }
                            sx={{
                              transform:
                                selectedTab === 0 || isMobile
                                  ? "rotate(0deg)"
                                  : "rotate(90deg)",
                              transition: isMobile ? "none" : "transform .25s",
                            }}
                          />
                        }
                        iconPosition="end"
                        tabIndex={-1}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                      <Tab
                        key={"popular"}
                        disableRipple={isMobile}
                        sx={styles.tab(selectedTab === 1, isMobile)}
                        onMouseOver={() => !isMobile && handleFocus(1, "popular")}
                        onClick={() => isMobile && handleFocus(1, "popular")}
                        label={
                          t`Popular Searches` +
                          ` (${getFilteredOptionsTabLenght("popular")})`
                        }
                        icon={
                          <KeyboardArrowRightIcon
                            color={
                              selectedTab === 1 ? "white" : "secondary"
                            }
                            sx={{
                              transform:
                                selectedTab === 1 || isMobile
                                  ? "rotate(0deg)"
                                  : "rotate(90deg)",
                              transition: isMobile ? "none" : "transform .25s",
                            }}
                          />
                        }
                        iconPosition="end"
                        tabIndex={-1}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                      {uniqueOptions?.map((region, index) => {
                          const idx = index + 2;
                          return (
                            <Tab
                              key={idx}
                              disableRipple={isMobile}
                              sx={styles.tab(
                                selectedTab === idx,
                                isMobile
                              )}
                              onMouseOver={() =>
                                !isMobile && handleFocus(idx, region.regionCode)
                              }
                              onClick={() =>
                                isMobile && handleFocus(idx, region.regionCode)
                              }
                              label={`${
                                region.regionCodeTranslation
                              } (${getFilteredOptionsTabLenght(
                                region.regionCode
                              )})`}
                              icon={
                                <KeyboardArrowRightIcon
                                  color={
                                    selectedTab === idx
                                      ? "white"
                                      : "secondary"
                                  }
                                  sx={{
                                    transform:
                                      selectedTab === idx || isMobile
                                        ? "rotate(0deg)"
                                        : "rotate(90deg)",
                                    transition: isMobile
                                      ? "none"
                                      : "transform .25s",
                                  }}
                                />
                              }
                              iconPosition="end"
                              tabIndex={-1}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                      )})}
                    </Tabs>
                  )}
                  {((isMobile && selectedTab !== null) || !isMobile) && (
                    <Box
                      ref={listRef}
                      sx={styles.boxRoot(
                        isMobile,
                        filteredOptions.length === 0
                      )}
                    >
                      {filteredOptions?.length > 0 ? (
                        filteredOptions?.map((airport, idx) => (
                          <ListItemButton
                            key={idx}
                            ref={handleListItemRef(idx)}
                            sx={{
                              ...styles.listItem(
                                airport.code === selectedValue?.code
                              ),
                              backgroundColor:
                                idx === highlightedIndex
                                  ? "#e3f2fd"
                                  : undefined, // light blue bg for focus
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (selectedValue?.code !== airport.code) {
                                onChange(airport);
                              }
                              setOpen(false);
                            }}
                          >
                            <ListItemText
                              primary={airport.name}
                              slotProps={{
                                primary: {
                                  fontFamily: "OpenSansMedium",
                                },
                                secondary: {
                                  fontFamily: "monospace",
                                },
                              }}
                              secondary={`${airport.code}`}
                            />
                          </ListItemButton>
                        ))
                      ) : (
                        <ListItemText
                          sx={{ py: 1, px: 2 }}
                          primary={
                            noOptionsText
                              ? noOptionsText
                              : t`No airports found...`
                          }
                          slotProps={{
                            primary: {
                              color: "rgba(76,76,76,0.8)",
                              textAlign: isMobile ? "left" : "center",
                            },
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Popper>
          ) : (
            <Popper
              open={open}
              anchorEl={inputAnchorRef.current}
              sx={{
                minWidth: "auto",
                width: inputAnchorRef.current?.offsetWidth,
              }}
              placement={
                label === "to" || label === "parra" || label === "vers"
                  ? "bottom-end"
                  : "bottom-start"
              }
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 2],
                  },
                },
              ]}
              style={{ zIndex: 1500, pointerEvents: "auto" }}
            >
              <Box
                component={Paper}
                className={"azo-airport-list-popup"}
                elevation={2}
              >
                <Box sx={{ py: 1, px: 2 }}>
                  <ListItemText
                    primary={
                      noOptionsText ? noOptionsText : t`No airports found...`
                    }
                    slotProps={{
                      primary: {
                        color: "rgba(76,76,76,0.8)",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Popper>
          ))}
      </Box>
    );
};


const styles = {
    root: (isMobile = false) => ({
        bgcolor: 'background.paper',
        display: 'flex',
        height: '100%',
        maxHeight: 500,
        width: '100%',
        border: isMobile ? 'none' : '1px solid #007DB3FF',
        borderRadius: isMobile ? 'none' : '5px',
        position: 'relative',
        overflow: 'hidden',
    }),
    rootTab: (isMobile = false) => ({
        overflow: 'unset',
        borderRight: 1,
        borderColor: 'divider',
        textAlign: 'left',
        width: isMobile ? '100%' : 230
    }),
    tab: (isSelected, isMobile = false) => ({
        backgroundColor: isSelected ? '#007DB3FF' : 'inherit',
        color: isSelected ? 'white !important' : 'black !important',
        fontFamily: 'OpenSansMedium',
        justifyContent: 'space-between',
        justifyItems: 'left',
        padding: '0px 10px',
        minWidth: '100%',
        width: isMobile ? '100%' : 230,
        minHeight: '44px',
        '&:hover': {
            backgroundColor: isSelected ? '#007DB3FF' : '#0000000A',
            color: isSelected ? '#FFFFFF' : '#007DB3FF',
        },
    }),
    listItem: (isSelected) => ({
        backgroundColor: isSelected ? 'rgba(0,125,179,0.2)' : 'inherit',
        fontFamily: 'OpenSansMedium',
        '&:hover': {
            backgroundColor: '#0000000A',
            color: '#000',
        }
    }),
    boxRoot: (isMobile = false, noOptions = false) => ({
        flexGrow: 1,
        minHeight: isMobile ? 'auto' : 442,
        maxHeight: isMobile ? 'auto' : 442,
        overflow: 'auto',
        transform: isMobile && !noOptions ? 'translateX(100%)' : 'translateX(0)',
        transition: isMobile && !noOptions ? 'transform 0.3s ease-in-out' : 'none',
        animation: isMobile && !noOptions  ? 'slideIn 0.3s forwards' : 'none',
        '&::-webkit-scrollbar': {
            width: 6,
        },
        '&::-webkit-scrollbar-track': {
            background: '#9e9e9e !important',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#007db3 !important',
            borderRadius: 5,
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#003e55 !important',
        },
        '@keyframes slideIn': {
            from: {
                transform: 'translateX(100%)'
            },
            to: {
                transform: 'translateX(0)'
            }
        }
    })
}

AirportSelector.airportShape = PropTypes.shape({
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    cityName: PropTypes.string,
    countryCode: PropTypes.string,
    countryName: PropTypes.string,
    regionName: PropTypes.string,
    azores: PropTypes.bool,
    destinations: PropTypes.arrayOf(PropTypes.string),
});

AirportSelector.propTypes = {
    options: PropTypes.arrayOf(AirportSelector.airportShape).isRequired,
    variant: PropTypes.oneOf(['standard', 'outlined', 'filled', 'azo']),
    label: PropTypes.string,
    placeholder: PropTypes.string,
    leftRadius: PropTypes.bool,
    rightRadius: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([
        AirportSelector.airportShape,
        PropTypes.oneOf([null])
    ]),
    autoFocus: PropTypes.bool,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    inputRef: PropTypes.any,
    noOptionsText: PropTypes.string,
    optionField: PropTypes.string,
    compressed: PropTypes.bool,
    hideIcon: PropTypes.bool,
    flat: PropTypes.bool,
    error: PropTypes.bool,
    noBorder: PropTypes.bool,
};

export default AirportSelector;